import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, CheckCircle, ArrowLeft, KeyRound } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI } from '../services/api';
import { usePageMeta } from '../services/seo';
import { useT } from '../services/i18n';

const ForgotPasswordPage = () => {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  usePageMeta({ title: t('auth.forgotTitle'), description: t('auth.forgotMeta'), noindex: true });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/\S+@\S+\.\S+/.test(email)) { setError(t('auth.emailAddressInvalid')); return; }
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(err.response?.status === 429 ? t('auth.tooMany') : t('auth.genericError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block"><Logo variant="light" size="lg" className="mx-auto" /></Link>
          <h1 className="mt-4 text-2xl font-bold text-gray-900" data-testid="forgot-title">{t('auth.forgotTitle')}</h1>
          <p className="mt-2 text-sm text-gray-600">{t('auth.forgotIntro')}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-8" data-testid="forgot-password-page">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="w-14 h-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center mx-auto mb-4"><CheckCircle size={30} /></div>
              <p className="text-gray-800 font-medium">{t('auth.checkInbox')}</p>
              <p className="text-sm text-gray-600 mt-2">{t('auth.sentBefore')} <strong>{email}</strong>{t('auth.sentAfter')}</p>
              <p className="text-xs text-gray-400 mt-3">{t('auth.spam')}</p>
              <Link to="/connexion" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-4 py-2 min-h-[36px]" data-testid="forgot-back-login"><ArrowLeft size={14} /> {t('auth.backLogin')}</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
              {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm" data-testid="forgot-password-error">{error}</div>}
              <div>
                <Label htmlFor="email">{t('auth.emailAddress')}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input id="email" type="email" data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10" placeholder={t('auth.emailPh')} autoFocus />
                </div>
              </div>
              <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-orange-500 hover:bg-orange-600">
                {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <KeyRound size={16} className="mr-2" />} {t('auth.sendLink')}
              </Button>
              <Link to="/connexion" className="flex items-center justify-center gap-1 text-sm text-blue-600 hover:underline" data-testid="forgot-back-login"><ArrowLeft size={14} /> {t('auth.backLogin')}</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
